import type { CSSProperties, ReactNode } from "react";
import { FP_SHOW_WHEN_ATTRIBUTE } from "@forgepoint/shared";
import { cn } from "@/lib/utils";
import { FpShowWhen } from "./FpShowWhen";
import { containerStyle, readContainerValues } from "./fpContainer";
import type { FpCommonProps } from "./types";

/** One column of an `<fp-columns>` row: its content and its resolved box. */
export interface FpColumn {
  content: ReactNode;
  style: CSSProperties;
  /** The cell's editor node id when the column is an `<fp-cell>`. */
  nodeId?: string;
}

interface FpColumnsProps extends FpCommonProps {
  /** The resolved columns, in document order. */
  columns: FpColumn[];
}

/**
 * Renders `<fp-columns>`: a flex row whose children are its columns. The row
 * keeps any class its caller injects - the editor's hidden-block marker, say -
 * on top of its own layout classes.
 * @param props - The resolved columns, the container style, and any class
 * @returns The columns row
 */
export function FpColumns({
  columns,
  className,
  style,
  "data-fp-node": dataFpNode,
}: FpColumnsProps) {
  return (
    <div
      className={cn("flex items-start", className)}
      style={{ gap: "16px", ...style }}
      data-fp-node={dataFpNode}
    >
      {columns.map((column, index) => (
        <div key={index} style={column.style} data-fp-node={column.nodeId}>
          {column.content}
        </div>
      ))}
    </div>
  );
}

/**
 * Resolves an `<fp-columns>` element's direct children into columns: an
 * `<fp-cell>` becomes one column holding every child of the cell and styled by
 * the cell's own container props, any other child becomes one column holding
 * itself. The `widths` list applies by position across both forms.
 * @param element - The `<fp-columns>` element
 * @param keyPrefix - The React key prefix for the converted content
 * @param renderElement - Converts one child element to React
 * @param renderChildren - Converts a cell's child nodes to React
 * @returns The resolved columns, in document order
 */
export function buildFpColumns(
  element: Element,
  keyPrefix: string,
  renderElement: (child: Element, key: string) => ReactNode,
  renderChildren: (parent: Node, childKeyPrefix: string) => ReactNode[],
): FpColumn[] {
  const widths = parseWidths(element.getAttribute("widths"));
  return Array.from(element.children).map((child, index) => {
    const key = `${keyPrefix}-col-${index}`;
    if (!isColumnCell(child)) {
      return {
        content: renderElement(child, key),
        style: columnSizing(widths[index]),
      };
    }
    return cellColumn(child, key, columnSizing(widths[index]), renderChildren);
  });
}

/**
 * One column standing in for an `<fp-cell>`. A gated cell keeps its sizing on
 * the column but moves its painted box inside the gate, so a hidden cell paints
 * nothing while the surviving columns keep their declared widths.
 * @param cell - The `<fp-cell>` element standing in for a column
 * @param key - The React key for the column
 * @param sizing - The column's positional flex sizing
 * @param renderChildren - Converts the cell's child nodes to React
 * @returns The resolved column
 */
function cellColumn(
  cell: Element,
  key: string,
  sizing: CSSProperties,
  renderChildren: (parent: Node, childKeyPrefix: string) => ReactNode[],
): FpColumn {
  const box = {
    ...CELL_COLUMN_FLOW,
    ...containerStyle(readContainerValues(cell)),
  };
  const nodeId = cell.getAttribute("data-fp-node") ?? undefined;
  const children = renderChildren(cell, key);
  const condition = cell.getAttribute(FP_SHOW_WHEN_ATTRIBUTE);
  if (condition === null) {
    return { content: children, style: { ...sizing, ...box }, nodeId };
  }
  return {
    content: (
      <FpShowWhen key={`when-${key}`} condition={condition}>
        <div style={box} data-fp-node={nodeId}>
          {children}
        </div>
      </FpShowWhen>
    ),
    style: sizing,
  };
}

/**
 * A cell column stacks its children, so `gap` between them resolves and an
 * inline child still owns its own line.
 */
const CELL_COLUMN_FLOW: CSSProperties = {
  display: "flex",
  flexDirection: "column",
};

/**
 * Whether a direct child of `<fp-columns>` is a cell holding a whole column.
 * @param child - The direct child element
 * @returns True when the child is an `<fp-cell>`
 */
function isColumnCell(child: Element): boolean {
  return child.tagName.toLowerCase() === "fp-cell";
}

/**
 * Splits the `widths` attribute into per-column CSS widths.
 * @param widths - The raw comma-separated attribute, or null when absent
 * @returns The per-column widths, empty when absent
 */
function parseWidths(widths: string | null): string[] {
  if (widths === null) {
    return [];
  }
  return widths
    .split(",")
    .map((width) => width.trim())
    .filter((width) => width !== "");
}

/**
 * The flex sizing for one column: its declared width, or an equal share.
 * @param width - The column's declared width, or undefined when unstated
 * @returns The column's sizing style
 */
function columnSizing(width: string | undefined): CSSProperties {
  if (width === undefined) {
    return { flex: "1 1 0", minWidth: 0 };
  }
  return { flex: `0 1 ${width}`, minWidth: 0 };
}
